import React, { useRef, useEffect, useState } from "react";
import QRCodeStyling from "qr-code-styling";
import { useLang } from "../LanguageContext";

const COLORS = [
  { key: "black",   value: "#000000" },
  { key: "grey",    value: "#808080" },
  { key: "magenta", value: "#e9007a" },
  { key: "blue",    value: "#00a8e3" },
];

const DOT_STYLES    = ["squares", "dots", "rounded"];
const CORNER_STYLES = ["square",  "dots", "rounded"];

function cornerSquareType(s) { return s === "dots" ? "dot" : s === "rounded" ? "extra-rounded" : "square"; }
function cornerDotType(s)    { return s === "square" ? "square" : "dot"; }
function dotTypeFor(s)       { return s === "squares" ? "square" : s; }
function sanitize(url)       { return url.replace(/^https?:\/\//, "").replace(/[^a-zA-Z0-9._-]/g, "_"); }

const VALID_TYPES = ["image/png", "image/jpeg"];

function QRCodeGenerator() {
  const { t } = useLang();
  const [url, setUrl]                     = useState("");
  const [dotStyle, setDotStyle]           = useState("squares");
  const [cornerStyle, setCorner]          = useState("square");
  const [color, setColor]                 = useState("black");
  const [logoEnabled, setLogoEnabled]     = useState(false);
  const [logoFile, setLogoFile]           = useState(null);
  const [logoObjectUrl, setLogoObjectUrl] = useState(null);
  const [logoError, setLogoError]         = useState("");

  const qrRef        = useRef(null);
  const qrCode       = useRef(null);
  const fileInputRef = useRef(null);

  const colorValue = COLORS.find(c => c.key === color)?.value ?? "#000000";

  useEffect(() => {
    qrCode.current = new QRCodeStyling({
      width: 260, height: 260, type: "canvas",
      data: "https://example.com",
      qrOptions:            { errorCorrectionLevel: "H" },
      dotsOptions:          { color: "#000000", type: "square" },
      cornersSquareOptions: { color: "#000000", type: "square" },
      cornersDotOptions:    { color: "#000000", type: "square" },
      imageOptions:         { crossOrigin: "anonymous", margin: 8, imageSize: 0.35 },
      backgroundOptions:    { color: "#ffffff" },
    });
    qrCode.current.append(qrRef.current);
    return () => { if (qrRef.current) qrRef.current.innerHTML = ""; };
  }, []);

  useEffect(() => {
    if (!qrCode.current) return;
    qrCode.current.update({
      data:                 url || "https://example.com",
      image:                logoEnabled && logoObjectUrl ? logoObjectUrl : "",
      dotsOptions:          { color: colorValue, type: dotTypeFor(dotStyle) },
      cornersSquareOptions: { color: colorValue, type: cornerSquareType(cornerStyle) },
      cornersDotOptions:    { color: colorValue, type: cornerDotType(cornerStyle) },
    });
  }, [url, dotStyle, cornerStyle, colorValue, logoEnabled, logoObjectUrl]);

  useEffect(() => {
    return () => { if (logoObjectUrl) URL.revokeObjectURL(logoObjectUrl); };
  }, [logoObjectUrl]);

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!VALID_TYPES.includes(file.type)) {
      setLogoError(t("qrCode.logoError"));
      e.target.value = "";
      return;
    }
    setLogoError("");
    setLogoFile(file);
    setLogoObjectUrl(URL.createObjectURL(file));
  };

  const styleLabel = (key) => {
    const map = { squares: "styleSquares", dots: "styleDots", rounded: "styleRounded" };
    return t(`qrCode.${map[key] ?? key}`);
  };

  const cornerLabel = (key) => {
    const map = { square: "cornerSquare", dots: "cornerDots", rounded: "cornerRounded" };
    return t(`qrCode.${map[key] ?? key}`);
  };

  return (
    <div className="qr-page">
      <div className="page-section-header">
        <span className="section-label">{t("qrCode.label")}</span>
        <div className="subhead">{t("qrCode.title")}</div>
        <p className="section-intro-inner">{t("qrCode.intro")}</p>
      </div>

      <div className="qr-layout">
        <div className="tool-card qr-form-card">
          <div className="calc-field">
            <label>{t("qrCode.urlLabel")}</label>
            <input type="url" className="input-field" value={url} onChange={e => setUrl(e.target.value)} placeholder={t("qrCode.urlPlaceholder")} />
          </div>

          <div className="qr-field-group">
            <span className="calc-field label">{t("qrCode.dotStyle")}</span>
            <div className="qr-btn-group">
              {DOT_STYLES.map(s => (
                <button key={s} type="button" className={`qr-toggle${dotStyle === s ? " active" : ""}`} onClick={() => setDotStyle(s)}>
                  {styleLabel(s)}
                </button>
              ))}
            </div>
          </div>

          <div className="qr-field-group">
            <span className="calc-field label">{t("qrCode.cornerStyle")}</span>
            <div className="qr-btn-group">
              {CORNER_STYLES.map(s => (
                <button key={s} type="button" className={`qr-toggle${cornerStyle === s ? " active" : ""}`} onClick={() => setCorner(s)}>
                  {cornerLabel(s)}
                </button>
              ))}
            </div>
          </div>

          <div className="qr-field-group">
            <span className="calc-field label">{t("qrCode.colour")}</span>
            <div className="qr-color-group">
              {COLORS.map(c => (
                <button key={c.key} type="button" className={`qr-color-btn${color === c.key ? " active" : ""}`}
                  style={{ "--swatch": c.value }} onClick={() => setColor(c.key)} aria-label={c.key} />
              ))}
            </div>
          </div>

          <div className="qr-field-group">
            <span className="calc-field label">{t("qrCode.logoOverlay")}</span>
            <label className="qr-toggle-row">
              <input type="checkbox" checked={logoEnabled} onChange={e => setLogoEnabled(e.target.checked)} />
              <span>{t("qrCode.enableLogo")}</span>
            </label>
            {logoEnabled && (
              <div className="qr-logo-upload">
                <label htmlFor="qr-logo-input" className={`qr-upload-btn${logoFile ? " has-file" : ""}`}>
                  {logoFile ? logoFile.name : t("qrCode.chooseLogo")}
                </label>
                <input ref={fileInputRef} id="qr-logo-input" type="file" accept="image/png,image/jpeg" onChange={handleLogoFile} className="qr-file-hidden" />
                {logoError && <p className="qr-error">{logoError}</p>}
              </div>
            )}
          </div>
        </div>

        <div className="tool-card qr-preview-card">
          <div ref={qrRef} className="qr-canvas-wrap" />
          <button type="button" className="qr-download-btn" onClick={() => qrCode.current?.download({ name: sanitize(url), extension: "png" })} disabled={!url}>
            {t("qrCode.download")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QRCodeGenerator;
